import { type Metric, onCLS, onINP, onLCP } from "web-vitals";

import { isProduction } from "@settings";

/**
 * Core Web Vitals thresholds (ms for LCP/INP, unitless for CLS).
 * @see https://web.dev/articles/vitals
 */
const THRESHOLDS: Record<string, [good: number, needsImprovement: number]> = {
  LCP: [2500, 4000],
  INP: [200, 500],
  CLS: [0.1, 0.25],
};

const rate = (metric: Metric): string => {
  const [good, needsImprovement] = THRESHOLDS[metric.name] ?? [0, 0];
  if (metric.value <= good) return "good";
  if (metric.value <= needsImprovement) return "needs-improvement";
  return "poor";
};

const logMetric = (metric: Metric): void => {
  const value =
    metric.name === "CLS"
      ? metric.value.toFixed(3)
      : `${Math.round(metric.value)}ms`;

  // eslint-disable-next-line no-console
  console.info(
    `[web-vitals] ${metric.name}: ${value} (${rate(metric)})`,
    metric,
  );
};

/**
 * Measures Core Web Vitals (LCP, CLS, INP) and reports them.
 * In development the values are logged to the console; a custom `onReport`
 * handler can be provided to forward metrics elsewhere (e.g. an analytics beacon).
 */
export const reportWebVitals = (
  onReport: (metric: Metric) => void = logMetric,
): void => {
  if (!isProduction && onReport === logMetric) return;

  onLCP(onReport);
  onCLS(onReport);
  onINP(onReport);
};
